import { jsPDF } from 'jspdf'
import 'jspdf-autotable'
import utils from '@/mixins/utils.js'
import store from '@/store/store'
import { api } from '@/plugins/axios.js'
import { vue } from '@/main.js'
import {
  motivosOcorrencia,
  montarOcorrencia,
  montarPayloadOcorrencia,
  formatarOcorrenciaExibicao,
  temOcorrencia,
  normalizarOcorrencia,
  normalizarListaOcorrencias,
  normalizarCadastros,
} from './ocorrencias.js'

export {
  motivosOcorrencia,
  montarOcorrencia,
  montarPayloadOcorrencia,
  formatarOcorrenciaExibicao,
  temOcorrencia,
  normalizarOcorrencia,
  normalizarListaOcorrencias,
  normalizarCadastros,
}

/**
 * Retorna os cadastros de crianças e mulheres do Bolsa Família por equipe.
 * (Rota placeholder — ajustar quando o backend estiver pronto.)
 *
 * @param {number|string} cod_ibge
 * @param {number|string} nu_ine
 * @returns {Promise<Array>}
 */
export async function getCadastrosPorEquipe(cod_ibge, nu_ine) {
  try {
    const { data } = await api.get(
      `/app/bolsa-familia-criancas-mulheres/${cod_ibge}/equipe/${nu_ine}`
    )
    const lista = Array.isArray(data) ? data : []
    return normalizarCadastros(lista)
  } catch (err) {
    console.log(err)
    return []
  }
}

/**
 * Filtra cadastros pela microárea
 *
 * @param {Array} cadastros
 * @param {string|number} microArea
 * @returns {Array}
 */
export function filtrarPorMicroArea(cadastros, microArea) {
  if (!Array.isArray(cadastros)) return []
  if (microArea == null || microArea === '') return cadastros

  const micro = String(microArea)
  return cadastros.filter((item) => String(item?.micro_area ?? '') === micro)
}

/**
 * Salva ou atualiza a ocorrência (PUT).
 * (Rota placeholder — ajustar quando o backend estiver pronto.)
 *
 * @param {number|string} cod_ibge
 * @param {Object} payload
 * @returns {Promise<*>}
 */
export async function salvarOcorrencia(cod_ibge, payload) {
  try {
    const { data } = await api.put(
      `/app/bolsa-familia-criancas-mulheres/${cod_ibge}/ocorrencia`,
      payload
    )
    return data
  } catch (err) {
    console.log(err)
    throw err
  }
}

/**
 * Número da vigência atual (1 = jan-jun, 2 = jul-dez).
 *
 * @returns {number}
 */
export function getVigenciaNumeroAtual() {
  const mes = new Date().getMonth() + 1
  return mes <= 6 ? 1 : 2
}

/**
 * Ano corrente.
 *
 * @returns {number}
 */
export function getAnoAtual() {
  return new Date().getFullYear()
}

/**
 * Extrai dados_ms (objeto, array ou JSON string).
 *
 * @param {Array|Object|string|null} dadosMs
 * @returns {Object|null}
 */
export function normalizarDadosMs(dadosMs) {
  if (!dadosMs) return null

  if (typeof dadosMs === 'string') {
    const texto = dadosMs.trim()
    if (!texto || texto === '[]' || texto === '{}') return null

    try {
      return normalizarDadosMs(JSON.parse(texto))
    } catch (err) {
      return null
    }
  }

  if (Array.isArray(dadosMs)) {
    if (dadosMs.length === 0) return null
    return normalizarDadosMs(dadosMs[0])
  }

  if (typeof dadosMs === 'object') {
    const vig = dadosMs.vig ?? dadosMs.vigencia
    const ano = dadosMs.ano

    if (vig == null || vig === '' || ano == null || ano === '') {
      return null
    }

    const vigNum = Number(vig)
    const anoNum = Number(ano)

    if (Number.isNaN(vigNum) || Number.isNaN(anoNum)) return null

    return {
      ...dadosMs,
      vig: vigNum,
      ano: anoNum,
      acomp:
        dadosMs.acomp != null && dadosMs.acomp !== ''
          ? Number(dadosMs.acomp)
          : null,
      peso: dadosMs.peso ?? null,
      altura: dadosMs.altura ?? null,
      ult_acomp:
        dadosMs.ult_acomp ??
        dadosMs.dt_acomp ??
        dadosMs.acompanhamento ??
        null,
    }
  }

  return null
}

/**
 * Totalmente (laranja): vigencia/ano do cadastro = atuais e há peso e altura.
 *
 * @param {Object} item
 * @returns {boolean}
 */
export function isVerde(item) {
  const vigencia = Number(item?.vigencia)
  const ano = Number(item?.ano)

  return (
    vigencia === getVigenciaNumeroAtual() &&
    ano === getAnoAtual() &&
    item?.peso != null &&
    item.peso !== '' &&
    item?.altura != null &&
    item.altura !== ''
  )
}

/**
 * Verificado (verde): dados_ms com acomp = 1 e vigência/ano atuais.
 *
 * @param {Object} item
 * @returns {boolean}
 */
export function isVerificado(item) {
  const ms = normalizarDadosMs(item?.dados_ms)
  if (!ms) return false

  return (
    Number(ms.acomp) === 1 &&
    getVigenciaNumeroAtual() === ms.vig &&
    getAnoAtual() === ms.ano
  )
}

/**
 * Cor da linha na tabela.
 * Prioridade: Verificado (verde) > Totalmente (laranja) > Parcialmente (vermelho).
 *
 * @param {Object} item
 * @returns {string}
 */
export function printTableRow(item) {
  if (isVerificado(item)) return 'green'
  if (isVerde(item)) return 'orange'
  return 'red'
}

/**
 * Valores de acompanhamento/peso/altura para exibição.
 * Com filtro Verificado, usa dados_ms; senão, campos do cadastro.
 *
 * @param {Object} item
 * @param {boolean} usarDadosMs
 * @returns {{ ult_acomp: *, peso: *, altura: * }}
 */
export function getCamposExibicao(item, usarDadosMs = false) {
  if (usarDadosMs) {
    const ms = normalizarDadosMs(item?.dados_ms)
    return {
      ult_acomp: ms?.ult_acomp ?? '',
      peso: ms?.peso ?? '',
      altura: ms?.altura ?? '',
    }
  }

  return {
    ult_acomp: item?.ult_acomp ?? '',
    peso: item?.peso ?? '',
    altura: item?.altura ?? '',
  }
}

/**
 * Filtra cadastros parcialmente preenchidos.
 *
 * @param {Array} cadastros
 * @returns {Array}
 */
export function exibirVermelhos(cadastros) {
  return cadastros.filter((item) => !isVerde(item) && !isVerificado(item))
}

/**
 * Filtra cadastros totalmente preenchidos (completo, ainda não verificado).
 *
 * @param {Array} cadastros
 * @returns {Array}
 */
export function exibirVerdes(cadastros) {
  return cadastros.filter((item) => isVerde(item) && !isVerificado(item))
}

/**
 * Filtra cadastros verificados (vigência/ano atuais = dados_ms).
 *
 * @param {Array} cadastros
 * @returns {Array}
 */
export function exibirVerificados(cadastros) {
  return cadastros.filter((item) => isVerificado(item))
}

/**
 * Vigência atual do mapa (1ª = jan-jun, 2ª = jul-dez).
 *
 * @returns {string}
 */
export function getVigenciaAtual() {
  const agora = new Date()
  const semestre = agora.getMonth() < 6 ? '1ª' : '2ª'
  return `${semestre} vigência de ${agora.getFullYear()}`
}

/**
 * Formata data para exibição no PDF.
 *
 * @param {*} data
 * @returns {string}
 */
function formatarData(data) {
  if (!data) return ''
  return utils.methods.formatDate(data)
}

/**
 * Converte altura para cm quando vier em metros (ex.: 1.65 → 165).
 *
 * @param {string|number|null} altura
 * @returns {string}
 */
function formatarAltura(altura) {
  if (altura == null || altura === '') return ''

  const valor = Number(String(altura).replace(',', '.'))
  if (Number.isNaN(valor)) return String(altura)
  if (valor > 0 && valor < 3) return String(Math.round(valor * 100))

  return String(altura)
}

/**
 * Agrupa cadastros por endereço + profissional (não há código familiar na API).
 *
 * @param {Array} dados
 * @returns {Array}
 */
function agruparPorFamilia(dados) {
  if (!Array.isArray(dados) || dados.length === 0) return []

  const grupos = new Map()

  dados.forEach((item) => {
    const chave = `${item.endereco || ''}|${item.no_profissional || ''}`

    if (!grupos.has(chave)) {
      grupos.set(chave, {
        endereco: item.endereco || '',
        profissional: item.no_profissional || '',
        membros: [],
      })
    }

    grupos.get(chave).membros.push(item)
  })

  return Array.from(grupos.values())
}

/**
 * Monta as linhas do body do autoTable (família + membros).
 *
 * @param {Array} familias
 * @param {boolean} usarDadosMs
 * @returns {Array}
 */
function montarBodyPdf(familias, usarDadosMs = false) {
  const body = []

  familias.forEach((familia) => {
    body.push([
      {
        content: [
          `Endereço: ${familia.endereco || '-'}`,
          `Profissional: ${familia.profissional || '-'}`,
        ].join('   |   '),
        colSpan: 15,
        styles: {
          fillColor: [220, 220, 220],
          textColor: [0, 0, 0],
          fontStyle: 'bold',
          halign: 'left',
          fontSize: 9,
        },
      },
    ])

    familia.membros.forEach((membro) => {
      const vacinaPendente = membro.vacina_pendente
      const campos = getCamposExibicao(membro, usarDadosMs)
      const row = [
        membro.nis || '',
        membro.nu_cns || '',
        membro.no_cidadao || '',
        formatarData(membro.dt_nascimento),
        formatarData(campos.ult_acomp),
        '',
        campos.peso || '',
        formatarAltura(campos.altura),
        '',
        vacinaPendente ? 'Não' : campos.ult_acomp ? 'Sim' : '',
        vacinaPendente || '',
        '',
        '',
        '',
        formatarData(membro.dt_dum),
      ]
      row._rowColor = printTableRow(membro)
      body.push(row)
    })
  })

  return body
}

/**
 * Gera o PDF do Mapa de Acompanhamento do Bolsa Família (Crianças e Mulheres).
 *
 * @param {Array} cadastros
 * @param {Object} selectEquipe
 * @param {{ usarDadosMs?: boolean }} [opcoes]
 */
export function generatePdf(cadastros, selectEquipe, opcoes = {}) {
  const usarDadosMs = Boolean(opcoes.usarDadosMs)
  const vigencia = getVigenciaAtual()
  const titlePdf = `MAPA DE ACOMPANHAMENTO DO BOLSA FAMÍLIA - ${vigencia}`
  const titlePdfSave = utils.methods.titlePdf(
    'Bolsa Família Crianças e Mulheres',
    selectEquipe?.no_equipe
  )
  const descriptionPdf = 'Crianças e mulheres beneficiárias do Bolsa Família'

  const familias = agruparPorFamilia(cadastros)
  const body = montarBodyPdf(familias, usarDadosMs)

  const headersPdf = [
    [
      {
        content: 'TODOS',
        colSpan: 6,
        styles: { halign: 'center', fillColor: [204, 204, 204] },
      },
      {
        content: 'CRIANÇA',
        colSpan: 5,
        styles: { halign: 'center', fillColor: [204, 204, 204] },
      },
      {
        content: 'MULHER',
        colSpan: 4,
        styles: { halign: 'center', fillColor: [204, 204, 204] },
      },
    ],
    [
      'NIS',
      'CNS',
      'Nome',
      'Data de\nnascimento',
      'Data de\nacompanhamento\n(A)',
      'Ocorrência Identificada - Não acompanhamento',
      'Peso\nem kg\n(B)',
      'Altura\nem cm\n(B)',
      'Ocorrência Identificada - Não Informação Nutricional',
      'Vacinação em dia? (B)',
      'Ocorrência Identificada - Não vacinação',
      'Informação Gestacional (C)',
      'Se gestante - Realizou o Pré-Natal? (D)',
      'Ocorrência Identificada - Não Pré-Natal',
      'DUM (D)',
    ],
  ]

  const doc = new jsPDF({
    orientation: 'l',
    unit: 'pt',
    format: 'a3',
    ...utils.methods.encryptionPdf(),
  })

  const brasao = store.state.infoMunicipio?.brasao || ''

  utils.methods
    .fileToBase64(brasao)
    .catch(() => '')
    .then((base64data) => {
      let verticalPosition = utils.methods.headerPdf(
        doc,
        base64data || '',
        titlePdf,
        descriptionPdf,
        200,
        'l'
      )

      verticalPosition = utils.methods.bodyPdf(
        doc,
        [
          {
            label: 'Município',
            value: `${store.state.cod_ibge} - ${store.state.cidade}`,
          },
          {
            label: 'Equipe',
            value: selectEquipe?.no_equipe,
          },
          {
            label: 'CNES',
            value: selectEquipe?.nu_cnes,
          },
          {
            label: 'INE',
            value: selectEquipe?.ine_esf_eap || selectEquipe?.nu_ine,
          },
        ],
        verticalPosition,
        'l'
      )

      const pageWidth = doc.internal.pageSize.getWidth()
      const marginX = 40
      const tableWidth = pageWidth - marginX * 2

      // Colunas estreitas (NIS, CNS, datas, peso, altura) forçam quebra do título em ~3 linhas
      // e liberam espaço para aumentar a fonte. Índices: 0,1,3,4,6,7
      const proporcoes = [6, 7, 17, 5, 7, 7, 3, 3, 8, 5, 7, 7, 8, 5, 5]
      const columnStyles = proporcoes.reduce((acc, proporcao, index) => {
        acc[index] = {
          cellWidth: (tableWidth * proporcao) / 100,
          overflow: 'linebreak',
        }
        return acc
      }, {})

      doc.autoTable({
        head: headersPdf,
        body,
        startY: verticalPosition,
        theme: 'grid',
        showHead: 'everyPage',
        margin: {
          left: marginX,
          right: marginX,
          bottom: 130,
        },
        tableWidth,
        styles: {
          fontSize: 9,
          cellPadding: 2,
          overflow: 'linebreak',
          valign: 'middle',
        },
        headStyles: {
          fillColor: '#ccc',
          fontSize: 8,
          textColor: '#000',
          halign: 'center',
          valign: 'middle',
          cellPadding: 2,
          overflow: 'linebreak',
        },
        bodyStyles: {
          fontSize: 9,
          textColor: '#000',
          halign: 'left',
        },
        columnStyles,
        willDrawCell: function (data) {
          if (data.row.section === 'head') {
            doc.setTextColor(0, 0, 0)
            return
          }

          const raw = data.row.raw
          if (raw?.[0]?.colSpan) {
            doc.setTextColor(0, 0, 0)
            return
          }

          if (raw?._rowColor === 'green') {
            doc.setTextColor(2, 99, 57)
            return
          }

          if (raw?._rowColor === 'orange') {
            doc.setTextColor(255, 102, 0)
            return
          }

          if (raw?._rowColor === 'red') {
            doc.setTextColor(194, 10, 16)
            return
          }

          doc.setTextColor(0, 0, 0)
        },
      })

      const pageHeight = doc.internal.pageSize.getHeight()
      const pageWidthFooter = doc.internal.pageSize.getWidth()
      const lineLength = 280
      const lineStartX = (pageWidthFooter - lineLength) / 2
      const espacoFooter = 120
      let posicaoFooter = doc.autoTable.previous.finalY

      if (posicaoFooter + espacoFooter > pageHeight - 20) {
        doc.addPage()
        posicaoFooter = 40
      }

      utils.methods.footerPdf(
        doc,
        posicaoFooter,
        lineStartX,
        lineStartX + lineLength,
        'l'
      )

      doc.save(`${titlePdfSave}.pdf`)

      vue.$toast.success('PDF GERADO COM SUCESSO!', 'Sucesso:', {
        position: 'topRight',
      })
    })
    .catch((error) => {
      console.error(error)
      vue.$toast.error('ERRO AO GERAR PDF!', 'Erro:', {
        position: 'topRight',
      })
    })
}

export const dataFields = {
  Nome: 'no_cidadao',
  'Data de nascimento': {
    field: 'dt_nascimento',
    callback: (value) => {
      return value ? utils.methods.formatDate(value) : ''
    },
  },
  CPF: 'nu_cpf',
  CNS: 'nu_cns',
  Mãe: 'no_mae',
  NIS: 'nis',
  MA: 'micro_area',
  Peso: 'peso',
  Altura: 'altura',
  'Último acompanhamento': {
    field: 'ult_acomp',
    callback: (value) => {
      return value ? utils.methods.formatDate(value) : ''
    },
  },
  DUM: {
    field: 'dt_dum',
    callback: (value) => {
      return value ? utils.methods.formatDate(value) : ''
    },
  },
  'Vacina pendente': 'vacina_pendente',
  Endereço: 'endereco',
  Profissional: 'no_profissional',
}
